// src/components/navigation/Navigation.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Home, Heart, Award, Sparkles, Trophy, Users, BarChart2, ChevronLeft, User, Swords, TrendingUp, Ghost, Network } from 'lucide-react';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [currentActiveTab, setCurrentActiveTab] = useState(location.state?.activeTab || 'overview');
  
  // Listen for custom tab change events from screens
  useEffect(() => {
    const handleTabChange = (event) => {
      const { tab, path } = event.detail;
      
      // Only update if this event is for a path we care about
      if (path === '/league-overview' || path === '/my-journey') {
        setCurrentActiveTab(tab);
      }
    };
    
    // Add event listener
    window.addEventListener('fpl-tab-changed', handleTabChange);
    
    // Clean up
    return () => {
      window.removeEventListener('fpl-tab-changed', handleTabChange);
    };
  }, []);

  // Use a custom hook to detect URL changes and stay in sync with state
  useEffect(() => {
    // Function to update menu state from URL state
    const handleURLStateChange = () => {
      // Get the current location state
      const currentState = window.history.state;
      
      // Update our local tracking of the state
      if (currentState && currentState.activeTab) {
        setCurrentActiveTab(currentState.activeTab);
      } else if (location.pathname === '/league-overview' && !currentState?.activeTab) {
        // Default to 'overview' for league overview with no tab state
        setCurrentActiveTab('overview');
      } else if (location.pathname === '/my-journey' && !currentState?.activeTab) {
        // Default to 'profile' for my journey with no tab state
        setCurrentActiveTab('profile');
      }
    };
    
    // Call immediately to sync on mount
    handleURLStateChange();
    
    // Set up listener for history state changes
    window.addEventListener('popstate', handleURLStateChange);
    
    // Clean up listener on unmount
    return () => {
      window.removeEventListener('popstate', handleURLStateChange);
    };
  }, [location.pathname]);
  
  // Monitor state changes from direct location changes too
  useEffect(() => {
    // Update our local tracking of the state when the location state changes directly
    if (location.state?.activeTab) {
      setCurrentActiveTab(location.state.activeTab);
    }
  }, [location.state?.activeTab]);

  // Function to determine if we should show a back button
  const shouldShowBackButton = () => {
    // Don't show back button on the home/welcome page
    return location.pathname !== '/';
  };
  
  // Function to determine if a route is currently active
  const isRouteActive = (route) => {
    // If we're on the same path...
    if (location.pathname === route.path) {
      // If this route specifies an activeTab, check against our tracked active tab
      if (route.state?.activeTab) {
        return route.state.activeTab === currentActiveTab;
      }
      // If no tab specified for route but we're on this path, it's active only if no activeTab
      return !currentActiveTab;
    }
    return false;
  };
  
  // Enhanced routes with Home at top, followed by Player Selection, Personal Journey, and League Overview
  const routes = [
    // Home at the very top
    { id: 'home', path: '/', label: 'Home', icon: <Home size={20} />, type: 'route' },
    
    // Player Selection section
    { id: 'player-selection-section', title: 'Player Selection', type: 'section' },
    { id: 'select-player', path: '/journey-selector', label: 'Select Player', icon: <User size={20} />, state: { forceSelectManager: true }, type: 'route' },
    
    // Section title for Personal Journey
    { id: 'personal-section', title: 'Personal Journey', type: 'section' },
    
    // Personal journey routes (without Home)
    { id: 'my-profile', path: '/my-journey', label: 'My Profile', icon: <Users size={20} />, state: { activeTab: 'profile' }, type: 'route' },
    { id: 'my-journey', path: '/my-journey', label: 'Performance Journey', icon: <BarChart2 size={20} />, state: { activeTab: 'performance' }, type: 'route' },
    { id: 'rank-pressure',  path: '/my-journey', label: 'Rank Pressure Wave', icon: <TrendingUp size={20} />, state: { activeTab: 'rankpressure' },  type: 'route' },
    { id: 'bench-haunting', path: '/my-journey', label: 'Bench Haunting',     icon: <Ghost size={20} />,     state: { activeTab: 'benchhaunting' }, type: 'route' },
    { id: 'squad-lifecycle',path: '/my-journey', label: 'Squad Story',        icon: <Heart size={20} />,     state: { activeTab: 'squadstory' },    type: 'route' },

    // Section title for League Overview
    { id: 'league-section', title: 'League Overview', type: 'section' },

    // League overview routes
    { id: 'league-overview',       path: '/league-overview', label: 'League Dashboard',    icon: <BarChart2 size={20} />, state: { activeTab: 'overview' },       type: 'route' },
    { id: 'league-leaderboard',    path: '/league-overview', label: 'Leaderboard',         icon: <Trophy size={20} />,    state: { activeTab: 'leaderboard' },    type: 'route' },
    { id: 'league-heatmap',        path: '/league-overview', label: 'Season Heatmap',      icon: <Sparkles size={20} />,  state: { activeTab: 'heatmap' },        type: 'route' },
    { id: 'league-special-stats',  path: '/league-overview', label: 'Special Stats',       icon: <Award size={20} />,     state: { activeTab: 'specialStats' },   type: 'route' },
    { id: 'league-rivalry',        path: '/league-overview', label: 'Rivalry',             icon: <Swords size={20} />,    state: { activeTab: 'rivalry' },        type: 'route' },
    { id: 'league-constellation',  path: '/league-overview', label: 'League Constellation',icon: <Network size={20} />,   state: { activeTab: 'constellation' }, type: 'route' },
  ];
  
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };
  
  const navigateTo = (path, state) => {
    // Update the current active tab when navigating
    if (state?.activeTab) {
      setCurrentActiveTab(state.activeTab);
    } else {
      // If no activeTab specified in state, reset it (for Home and similar)
      setCurrentActiveTab(null);
    }
    navigate(path, { state });
    setIsOpen(false);
  };
  
  const journeyPages = ['/my-journey', '/league-overview', '/transfer-regrets', '/captain-hindsight', '/differential-king', '/rank-pressure', '/bench-haunting', '/squad-lifecycle', '/league-constellation'];

  return (
    <>
      {/* Fixed top bar — contains back button (left) and menu button (right).
          Page content must have pt-14 to clear this bar. */}
      <div
        className="fixed top-0 left-0 right-0 z-50 h-14 flex items-center justify-between px-4"
        style={{ backgroundColor: 'rgba(0, 0, 6, 0.75)', backdropFilter: 'blur(12px)', borderBottom: '1px solid var(--c-border)' }}
      >
        {/* Back button — only shown on inner pages */}
        {shouldShowBackButton() ? (
          <button
            onClick={() => {
              if (journeyPages.includes(location.pathname)) {
                navigate('/journey-selector', { state: { forceJourneySelection: true } });
              } else {
                navigate(-1);
              }
            }}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-white text-sm transition-colors"
            style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid var(--c-border)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'; }}
            aria-label="Go back"
          >
            <ChevronLeft size={16} />
            <span>{journeyPages.includes(location.pathname) ? 'Journey Selector' : 'Back'}</span>
          </button>
        ) : (
          <div /> /* spacer to keep menu button on the right */
        )}

        {/* Menu toggle */}
        <button
          onClick={toggleMenu}
          className="w-9 h-9 flex items-center justify-center rounded-full text-white transition-colors"
          style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid var(--c-border)' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'; }}
          aria-label="Toggle navigation menu"
        >
          {isOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>


      {/* Sliding sidebar */}
      <div
        className={`fixed inset-y-0 right-0 z-40 w-72 shadow-xl transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ backgroundColor: 'var(--c-surface-1)', borderLeft: '1px solid var(--c-border)', backdropFilter: 'blur(12px)' }}
      >
        <div className="pt-16 pb-16 px-6 h-full overflow-y-auto">
          <h3 className="text-xl font-bold text-white mb-6">FPL Wrapped</h3>

          <nav className="space-y-6">
            {routes.map((route) => (
              route.type === 'section' ? (
                <div key={route.id} className="pt-4 first:pt-0" style={{ borderTop: '1px solid var(--c-border)' }}>
                  <h4 className="text-xs uppercase tracking-wider font-semibold mb-2" style={{ color: 'var(--c-grey)' }}>
                    {route.title}
                  </h4>
                </div>
              ) : (
                <button
                  key={route.id}
                  onClick={() => navigateTo(route.path, route.state)}
                  className="w-full text-left p-3 rounded-lg flex items-center transition-colors"
                  style={
                    isRouteActive(route)
                      ? { backgroundColor: 'rgba(63,105,174,0.2)', color: 'white', border: '1px solid rgba(63,105,174,0.4)' }
                      : { color: 'var(--c-grey)', border: '1px solid transparent' }
                  }
                  onMouseEnter={(e) => { if (!isRouteActive(route)) { e.currentTarget.style.backgroundColor = 'var(--c-surface-2)'; e.currentTarget.style.color = 'white'; } }}
                  onMouseLeave={(e) => { if (!isRouteActive(route)) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--c-grey)'; } }}
                >
                  <span className="mr-3" style={{ color: isRouteActive(route) ? 'var(--c-blue)' : 'inherit' }}>{route.icon}</span>
                  {route.label}
                </button>
              )
            ))}
          </nav>
        </div>

        <div
          className="absolute bottom-0 left-0 w-full p-4"
          style={{ borderTop: '1px solid var(--c-border)', backgroundColor: 'var(--c-surface-1)' }}
        >
          <div className="text-xs text-center" style={{ color: 'var(--c-grey)' }}>
            FPL Wrapped © {new Date().getFullYear()}
          </div>
        </div>
      </div>

      {/* Overlay to close menu */}
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50"
          onClick={toggleMenu}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default Navigation;
